import {
  AccessibilityIcon,
  BrainIcon,
  CircleCheckIcon,
  ClipboardListIcon,
  HeartIcon,
  HeartPulseIcon,
  LockIcon,
  MessageCircleIcon,
  PhoneCallIcon,
  SearchCheckIcon,
  UserRoundIcon,
} from 'lucide-react'
import type { Feature } from '../types/content'

export const whyChooseUs: Feature[] = [
  {
    id: 'screened',
    icon: SearchCheckIcon,
    title: { en: 'Rigorously Screened Caregivers', es: 'Cuidadores Rigurosamente Evaluados' },
    body: {
      en: 'Thorough background checks, reference verification, and ongoing professional training for every caregiver.',
      es: 'Verificaciones de antecedentes exhaustivas, verificación de referencias y capacitación profesional continua para cada cuidador.',
    },
  },
  {
    id: 'licensed',
    icon: LockIcon,
    title: { en: 'Licensed, Bonded & Insured', es: 'Licenciado, Garantizado y Asegurado' },
    body: {
      en: 'Complete peace of mind with full liability coverage and professional certification at every level.',
      es: 'Tranquilidad completa con cobertura de responsabilidad completa y certificación profesional en cada nivel.',
    },
  },
  {
    id: 'plans',
    icon: ClipboardListIcon,
    title: { en: 'Personalized Care Plans', es: 'Planes de Cuidado Personalizados' },
    body: {
      en: "Every plan is custom-built around your loved one's unique needs, preferences, and daily schedule.",
      es: 'Cada plan se construye personalizadamente alrededor de las necesidades únicas de tu ser querido, preferencias y horario.',
    },
  },
  {
    id: 'communication',
    icon: PhoneCallIcon,
    title: { en: '24/7 Family Communication', es: 'Comunicación Familiar 24/7' },
    body: {
      en: 'Always reachable. We keep families informed and respond to any concern around the clock.',
      es: 'Siempre disponibles. Mantenemos a las familias informadas y respondemos a cualquier preocupación las 24 horas.',
    },
  },
  {
    id: 'team',
    icon: HeartIcon,
    title: { en: 'Compassionate, Experienced Team', es: 'Equipo Compasivo y Experimentado' },
    body: {
      en: 'Our caregivers bring genuine warmth and professional expertise to every single interaction.',
      es: 'Nuestros cuidadores traen calidez genuina y experiencia profesional a cada interacción.',
    },
  },
]

export const processSteps: Feature[] = [
  {
    id: 'consultation',
    icon: MessageCircleIcon,
    title: { en: 'Free Consultation', es: 'Consulta Gratuita' },
    body: {
      en: "Reach out for a no-obligation consultation. We'll listen, answer questions, and learn about your loved one's situation.",
      es: 'Comunícate para una consulta sin obligación. Escucharemos, responderemos preguntas y aprenderemos sobre la situación de tu ser querido.',
    },
  },
  {
    id: 'plan',
    icon: ClipboardListIcon,
    title: { en: 'Custom Care Plan', es: 'Plan de Cuidado Personalizado' },
    body: {
      en: 'We build a personalized care plan with your full involvement — services, schedule, and specific needs all customized.',
      es: 'Construimos un plan de cuidado personalizado con tu participación total — servicios, horario y necesidades específicas personalizadas.',
    },
  },
  {
    id: 'begins',
    icon: CircleCheckIcon,
    title: { en: 'Care Begins', es: 'El Cuidado Comienza' },
    body: {
      en: 'Your dedicated caregiver starts and we check in regularly, adjusting as needs evolve to ensure complete satisfaction.',
      es: 'Tu cuidador dedicado comienza y nos comunicamos regularmente, ajustándonos a medida que evolucionan las necesidades.',
    },
  },
]

export const whoWeCareFor: Feature[] = [
  {
    id: 'seniors',
    icon: UserRoundIcon,
    title: { en: 'Seniors & Elderly Adults', es: 'Personas Mayores y Adultos Mayores' },
    body: {
      en: 'Supporting independence and quality of life for seniors who want to age comfortably in their own homes.',
      es: 'Apoyando la independencia y calidad de vida para personas mayores que desean envejecer cómodamente en sus propios hogares.',
    },
  },
  {
    id: 'dementia',
    icon: BrainIcon,
    title: { en: "Alzheimer's & Dementia Care", es: 'Cuidado de Alzheimer y Demencia' },
    body: {
      en: 'Specialized, compassionate care with a calm and consistent routine for those living with memory conditions.',
      es: 'Cuidado especializado y compasivo con una rutina tranquila y consistente para aquellos que viven con condiciones de memoria.',
    },
  },
  {
    id: 'recovery',
    icon: AccessibilityIcon,
    title: { en: 'Post-Surgery & Recovery', es: 'Post-Cirugía y Recuperación' },
    body: {
      en: 'Temporary care and assistance during recovery periods to support healing and prevent complications at home.',
      es: 'Cuidado temporal y asistencia durante períodos de recuperación para apoyar la curación y prevenir complicaciones en casa.',
    },
  },
  {
    id: 'chronic',
    icon: HeartPulseIcon,
    title: { en: 'Chronic Illness Support', es: 'Apoyo para Enfermedades Crónicas' },
    body: {
      en: "Ongoing, reliable support for those managing diabetes, heart disease, COPD, Parkinson's, and more.",
      es: 'Apoyo continuo y confiable para aquellos que manejan diabetes, enfermedad cardíaca, EPOC, Parkinson y más.',
    },
  },
]
